// Estado del formulario del cuestionario (todo como texto, como lo teclea el vendedor) y su conversión al Cuestionario
// del contrato. La validación final es la MISMA del servidor (Cuestionario.safeParse); aquí además se piden las
// respuestas que el esquema no puede exigir (un sí/no sin contestar) y se traducen los errores a cada campo.
// Ojo (lección pagada): Number("") da 0. Un campo vacío es null, nunca 0.
import { Cuestionario, decisionAumento, modoAumento, negocioAPdbtAplica, ETIQUETA_NO_SE_TRANSFORMADOR, HORAS_NOCHE_MAX, HORAS_NOCHE_MIN, MENSAJE_RECORTE_LE_CABEN, MENSAJE_RECORTE_SIN_NOCHE, TARIFAS_CASA, TIPOS_CLIENTE, cuestionarioAmpliacion, esMediaTension, pdfTodaviaNoSale, recibosEsperados, usoDeCarga, fuenteDePago } from '@cotizador/compartido';
import type { AumentoAceptado, AumentoConsumo, Carga, DecisionAumento, ModoAumento, Estimacion, TipoCliente, FasesServicio, FiscalCuestionario, RespaldoCuestionario, Tarifa, TensionServicio, TipoEstructura, TipoProyecto } from '@cotizador/compartido';
import { cuadroConCurva, firmaConsumos, promedioDeCargas } from './consumos-ia.ts';

export interface FormaCarga {
  equipo: string;
  cantidad: string;
  w: string;
  horas_dia: string;
  meses_uso: string;
}

/** Un equipo que debe seguir funcionando con baterías (contrato v2: cuestionario.respaldo.cargas). Aislado (Diego, 13-oct
 *  23:00, respuesta w): sus horas de uso; vacías = las de omisión del equipo (usoPorOmision). 28-oct (Miguel puso 1 h «al día»
 *  y 2 «de noche» y se lo rechazó): se piden «Horas de día» y «Horas de noche» POR SEPARADO; al servidor va lo mismo que antes
 *  (horas_dia = día + noche, horas_noche = noche: horasAisladoDe). La clave es otra (horas_de_dia) para que un borrador de
 *  antes, con el total en horas_dia, no se lea como horas de día (migrarCargaRespaldo). */
export interface FormaCargaRespaldo {
  equipo: string;
  cantidad: string;
  w: string;
  /** Horas de uso con sol (0 a 24 − HORAS_NOCHE_MIN). */
  horas_de_dia?: string;
  /** Horas de uso sin sol (0 a HORAS_NOCHE_MAX): con ellas se dimensiona el banco del aislado. */
  horas_noche?: string;
}

export interface FormaCuestionario {
  nombre: string;
  telefono: string;
  correo: string;
  ciudad: string;
  estado: string;
  giro: string;
  titularEsCliente: boolean | null;
  titularNombre: string;
  tipo: TipoProyecto | null;
  esNuevo: boolean | null;
  metodoEstimacion: Estimacion['metodo'];
  kwhMes: string;
  /** z46-2 (P22): lo que paga a CFE con IVA y cada cuándo (metodoEstimacion 'pago'). */
  pagoMonto: string;
  pagoPeriodo: 'bimestral' | 'mensual';
  fuenteEstimacion: string;
  cargas: FormaCarga[];
  /** z58: huella del cuadro que el vendedor autorizó con «Usar estos consumos» ('' = sin autorizar; logica/consumos-ia.ts). */
  consumosAutorizados: string;
  cambio: 'igual' | 'aumenta' | 'disminuye';
  cambioDetalle: string;
  consumoFuturo: string;
  /** ADENDA z52-2: el aumento de consumo que el vendedor ACEPTÓ de la sugerencia de la IA (o de las reglas) con «Usar este aumento».
   *  Sin la decisión de la pantalla: esa se le pone al guardar, con los datos de ese momento (borradorCuestionario). */
  aumento: AumentoAceptado | null;
  ampliacion: boolean | null;
  existentePaneles: string;
  existentePanelW: string;
  existenteKwp: string;
  /** LEGADO: texto libre del inversor de antes de la variante "ampliación" (se conserva tal cual si el borrador lo trae). */
  existenteInversor: string;
  existenteNotas: string;
  /** Variante "ampliación" de la familia: el inversor que ya tiene, estructurado (uno; la plantilla imprime marca y kW). */
  existenteInvMarca: string;
  existenteInvModelo: string;
  existenteInvKw: string;
  existenteInvCantidad: string;
  existenteAnio: string;
  existenteInstalo: 'solarix' | 'otro' | null;
  existenteConexion: 'inversor_nuevo' | 'inversor_actual';
  dosMedidores: boolean | null;
  visita: boolean | null;
  facturacion: 'mensual' | 'bimestral' | 'no_se' | null;
  tarifa: Tarifa | 'no_se';
  /** Diego, 12-oct: régimen fiscal del cliente (solo negocio) y su tasa de ISR (persona física con actividad empresarial). */
  regimen: FiscalCuestionario['regimen'] | null;
  tasaIsr: string;
  /** Diego, 12-oct: tipo de estructura (losa/lámina por omisión) y el servicio de CFE (sin contestar = el motor lo deduce). */
  estructura: TipoEstructura;
  servicioFases: FasesServicio | null;
  servicioTension: TensionServicio | null;
  /** Diego, 12-oct tarde: la respuesta a la pregunta OBLIGATORIA de media tensión (220 o 440 V desde el 24-oct, 480 si venía de
   *  antes, o «No sé: lo veo en la visita»),
   *  aparte del servicio de baja tensión. Verificación adversarial de la etapa 2: si salía de servicioFases, un «Trifásico»
   *  tocado ANTES de elegir GDMTO dejaba la pregunta ya contestada con «No sé» sin que nadie la contestara. */
  transformador: TensionServicio | 'visita' | null;
  /** Solo al editar: la tarifa que leyó la IA del recibo (el cuestionario puede decir "No sé"). No se manda: decide si la
   *  pregunta del transformador aplica. */
  tarifaLeida: Tarifa | null;
  /** Bloqueo del verificador (28-oct): el parámetro `negocio_a_pdbt` de la cotización que se edita (decide, con la tarifa leída, en
   *  qué tarifa calcula el motor y por eso si se ofrece el aumento con IA). null = cotización NUEVA: nace con la regla
   *  (negocioAPdbtAplica: comercio e industria). */
  negocioAPdbt: boolean | null;
  notas: string;
  /** Baterías / aislado (contrato v2): qué se respalda. Sin equipos, no se manda respaldo (null). */
  respaldoTipo: 'respaldo' | 'hibrido';
  respaldoConPaneles: boolean | null;
  respaldoCargas: FormaCargaRespaldo[];
  respaldoNotas: string;
  /** TODO SE ELIGE DESDE EL INICIO (Diego, 13-oct, 22:45): tipo de cliente y qué se cotiza, en el primer paso. El cuestionario
   *  guarda lo de siempre (tipo, respaldo.tipo/con_paneles: ver tipoDeEnfoque) y, en baterías o aislado, el tipo de cliente en
   *  cliente.tipo (en paneles es el tipo del proyecto). */
  clienteTipo: ClienteTipo | null;
  queCotiza: QueCotiza | null;
  /** Ampliación de baterías (Diego, 27-oct-2026): la batería (llave de logica/ampliacion.ts → claveBateria), cuántas tiene y
   *  cuántas suma. No van en el cuestionario: con ellas nace `contenido.piezas` (lo demás se captura en su pantalla). */
  ampBateria: string;
  ampActuales: string;
  ampNuevas: string;
  /** RECORTE DE PUNTA (ADENDA z54, 29-oct-2026; solo industria): P1 «¿Trabaja entre semana de 6 a 10 de la noche?» y P2 «¿Por qué
   *  sin paneles nuevos?». «No» y «Le caben paneles» no dejan seguir (la batería no tendría qué recortar; en GDMTH van primero
   *  los paneles): no se pueden guardar. */
  recorteNoche: boolean | null;
  recorteSinPaneles: 'sin_techo' | 'ya_tiene' | 'le_caben' | null;
}

/** ADENDA z52-2: ¿el cuestionario ofrece «Calcular el aumento» con IA (y guarda el aumento aceptado)? La MISMA condición que el
 *  motor (aumentoConIAAplica): va a subir, con recibo, en casa, comercio o industria, sin ampliación ni dos medidores, y (bloqueo
 *  del verificador, 28-oct) en una tarifa donde el motor lo SUMA: la leída del recibo (o la del cuestionario; con `negocio_a_pdbt`,
 *  la futura). En GDBT, GDMTO y GDMTH no: ahí va «Consumo futuro al año», como en producción, y un aumento de antes no se manda. */
export function aumentoEnForma(f: Pick<FormaCuestionario, 'cambio' | 'esNuevo' | 'ampliacion' | 'dosMedidores' | 'tipo' | 'tarifa' | 'tarifaLeida' | 'negocioAPdbt'>): boolean {
  return modoAumentoForma(f) !== 'produccion';
}

/** Bloqueo del verificador (29-oct; z55(4)): CÓMO se ofrece (compartido → modoAumento). 'ia' = el diseño (la IA reemplaza «Consumo
 *  futuro al año» y calcula sola al salir de «¿Por qué?»); 'supuesta' = la tarifa todavía no se sabe (cuestionario «No sé» y sin
 *  recibo, p. ej. al CREAR un comercio): «Consumo futuro al año» como en producción y la IA solo con su botón; 'produccion' = sin IA. */
export function modoAumentoForma(f: Pick<FormaCuestionario, 'cambio' | 'esNuevo' | 'ampliacion' | 'dosMedidores' | 'tipo' | 'tarifa' | 'tarifaLeida' | 'negocioAPdbt'>): ModoAumento {
  const e = entradasAumento(f);
  return e ? modoAumento(e.c, e.t) : 'produccion';
}

/** Lo que DECIDIÓ la pantalla (se guarda con el aumento: el motor lo compara con lo que de verdad calcula). null en 'produccion'. */
export function decisionDeForma(f: Pick<FormaCuestionario, 'cambio' | 'esNuevo' | 'ampliacion' | 'dosMedidores' | 'tipo' | 'tarifa' | 'tarifaLeida' | 'negocioAPdbt'>): DecisionAumento | null {
  const e = entradasAumento(f);
  return e ? decisionAumento(e.c, e.t) : null;
}

function entradasAumento(f: Pick<FormaCuestionario, 'cambio' | 'esNuevo' | 'ampliacion' | 'dosMedidores' | 'tipo' | 'tarifa' | 'tarifaLeida' | 'negocioAPdbt'>) {
  if (!f.tipo || f.esNuevo !== false) return null;
  return {
    c: { tipo: f.tipo, tarifa: f.tarifa, consumo: { es_nuevo: false, cambio: f.cambio, cambio_detalle: null, consumo_futuro_kwh_anual: null }, ampliacion: { es_ampliacion: f.ampliacion === true, existente: null }, dos_medidores: f.dosMedidores === true },
    // una cotización NUEVA (negocioAPdbt null) nace con la regla de parametrosIniciales (comercio e industria)
    t: { tarifaLeida: f.tarifaLeida, negocioAPdbt: f.negocioAPdbt ?? negocioAPdbtAplica({ tipo: f.tipo }) },
  };
}

/** El aumento guardado, sin la decisión de la pantalla que lo guardó (al volver a guardar se decide otra vez con los datos de hoy). */
export function aumentoSinDecision(a: AumentoConsumo | null): AumentoAceptado | null {
  if (!a) return null;
  const { pantalla: _p, ...resto } = a;
  return resto;
}

export const CLIENTES_TIPO = TIPOS_CLIENTE;
export type ClienteTipo = TipoCliente;
/** peak_shaving: RECORTE DE PUNTA con baterías grandes en GDMTH (ADENDA z54, 29-oct-2026): solo industria, con sus 2 preguntas.
 *  ampliacion_baterias (Diego, 27-oct-2026): sumar baterías a un sistema que ya tiene (sin recibo ni opciones; formato piezas). */
export const QUE_COTIZA = ['paneles', 'paneles_baterias', 'baterias', 'aislado', 'ampliacion_baterias', 'peak_shaving'] as const;
export type QueCotiza = (typeof QUE_COTIZA)[number];
export const QUE_COTIZA_DISPONIBLE: Readonly<Record<QueCotiza, boolean>> = { paneles: true, paneles_baterias: true, baterias: true, aislado: true, ampliacion_baterias: true, peak_shaving: true };

/** ¿La forma es de una ampliación de baterías? (otro tipo de cotización: sin recibo, tarifa, consumo ni régimen fiscal). */
export function esAmpliacionForma(f: Pick<FormaCuestionario, 'tipo'>): boolean {
  return f.tipo === 'ampliacion_baterias';
}

/** Diego, 14-oct (z7, MANDA): a una INDUSTRIA no se le ofrecen baterías de respaldo (son módulos de casa) hasta que haya peak
 *  shaving con las baterías grandes: ni «Solo baterías de respaldo» ni «Paneles + baterías». 27-oct: tampoco «Ampliación de
 *  baterías» (sumar módulos de casa a un banco: la misma regla; solo casa y comercio). */
export const QUE_COTIZA_NO_INDUSTRIA: readonly QueCotiza[] = ['baterias', 'paneles_baterias', 'ampliacion_baterias'];
/** ADENDA z44/z45 (Diego, 27-oct-2026): «el aislado es solo para residencial»: a un comercio o una industria ya no se le ofrece
 *  (el servidor tampoco lo crea: MENSAJE_AISLADO_SOLO_CASA). Una cotización vieja que ya lo trae lo conserva (queCotizaOpciones). */
export const QUE_COTIZA_SOLO_CASA: readonly QueCotiza[] = ['aislado'];
export function queCotizaOfrecida(cliente: ClienteTipo | null, q: QueCotiza): boolean {
  // z54-1 (Diego, 28-oct): el recorte de punta es SOLO de industria (comercio GDMTH 24/7 queda como pregunta para Diego)
  if (q === 'peak_shaving') return cliente === 'industria';
  if ((cliente === 'comercio' || cliente === 'industria') && QUE_COTIZA_SOLO_CASA.includes(q)) return false;
  return !(cliente === 'industria' && QUE_COTIZA_NO_INDUSTRIA.includes(q));
}
/** Las tarjetas de «¿Qué se cotiza?» para ese tipo de cliente. Una cotización VIEJA que ya trae una que ya no se ofrece la
 *  conserva (se enseña la suya para que no se rompa: el vendedor la ve y puede cambiarla). */
export function queCotizaOpciones(cliente: ClienteTipo | null, actual: QueCotiza | null): QueCotiza[] {
  return QUE_COTIZA.filter((q) => queCotizaOfrecida(cliente, q) || q === actual);
}

/** El tipo del cuestionario (el esquema de siempre) que sale del enfoque elegido al inicio. */
export function tipoDeEnfoque(cliente: ClienteTipo, que: QueCotiza): TipoProyecto {
  if (que === 'paneles') return cliente;
  // recorte de punta: va como industria (giro, régimen, tensión del transformador, recibos GDMTH y CRM de siempre)
  if (que === 'peak_shaving') return 'industria';
  if (que === 'aislado') return 'aislado';
  if (que === 'ampliacion_baterias') return 'ampliacion_baterias';
  return 'baterias';
}

/** El enfoque de un cuestionario que ya existe (al editar o con un borrador local de antes de este paso). El tipo de cliente de
 *  unas baterías o un aislado es el que se guardó (cliente.tipo, que formaDesde deja en clienteTipo); en las de antes de que se
 *  guardara, se deduce: media tensión → industria; con giro o tarifa de negocio → comercio; si no, casa (antes todas volvían como
 *  casa). Qué se cotiza sale del TIPO del respaldo (lo que imprime el PDF), no de «¿lleva paneles?»: un «respaldo» con paneles
 *  es, para la plantilla, baterías de un cliente que YA tiene paneles (formatos/baterias/esquema.json), no «Paneles + baterías»
 *  (verificación adversarial del 14-oct, caso 3). */
export function enfoqueDe(
  f: Pick<FormaCuestionario, 'tipo' | 'giro' | 'respaldoTipo'> & Partial<Pick<FormaCuestionario, 'clienteTipo' | 'tarifa' | 'tarifaLeida' | 'recorteSinPaneles'>>,
): { clienteTipo: ClienteTipo | null; queCotiza: QueCotiza | null } {
  if (!f.tipo) return { clienteTipo: null, queCotiza: null };
  if (f.tipo === 'industria' && f.recorteSinPaneles) return { clienteTipo: 'industria', queCotiza: 'peak_shaving' };
  if (f.tipo === 'casa' || f.tipo === 'comercio' || f.tipo === 'industria') return { clienteTipo: f.tipo, queCotiza: 'paneles' };
  // ampliación de baterías: para quién es se guarda en cliente.tipo (casa si una de antes no lo trae)
  if (f.tipo === 'ampliacion_baterias') return { clienteTipo: f.clienteTipo ?? 'casa', queCotiza: 'ampliacion_baterias' };
  const tarifa = f.tarifa && f.tarifa !== 'no_se' ? f.tarifa : (f.tarifaLeida ?? null);
  const clienteTipo: ClienteTipo =
    f.clienteTipo ?? (esMediaTension(tarifa) ? 'industria' : f.giro.trim() || (tarifa && !TARIFAS_CASA.includes(tarifa)) ? 'comercio' : 'casa');
  if (f.tipo === 'aislado') return { clienteTipo, queCotiza: 'aislado' };
  return { clienteTipo, queCotiza: f.respaldoTipo === 'hibrido' ? 'paneles_baterias' : 'baterias' };
}

/** «¿Lleva paneles?» quedó distinto de lo que dice el enfoque (paneles + baterías = con paneles; solo baterías = sin paneles),
 *  o sin contestar: pasa con un borrador o una cotización de antes del paso «Qué cotizas». Entonces la pregunta SE ENSEÑA (si
 *  no, validar la exige y nadie la ve, o el PDF sale distinto de lo que dice la etiqueta). */
export function panelesFueraDeEnfoque(f: Pick<FormaCuestionario, 'tipo' | 'queCotiza' | 'respaldoConPaneles'>): boolean {
  if (f.tipo !== 'baterias') return false;
  if (f.queCotiza === 'paneles_baterias') return f.respaldoConPaneles !== true;
  if (f.queCotiza === 'baterias') return f.respaldoConPaneles !== false;
  return f.respaldoConPaneles === null;
}

/** ¿Este enfoque lleva paneles nuevos? (decide si aplica «Ampliación»). */
export function enfoqueConPaneles(que: QueCotiza | null): boolean {
  return que === 'paneles' || que === 'paneles_baterias';
}

/** Elegir (o cambiar) el enfoque en el primer paso PRECARGA el resto del cuestionario con lo que ya se sabe: tipo, respaldo
 *  (híbrido con paneles · respaldo sin paneles), aislado = sin CFE (se estima el consumo, sin recibos ni medidores), y lo que
 *  ya no aplica se apaga (solo baterías no lleva ampliación ni techo). */
export function conEnfoque(f: FormaCuestionario, cambio: { clienteTipo?: ClienteTipo; queCotiza?: QueCotiza }): FormaCuestionario {
  const n: FormaCuestionario = { ...f, ...cambio };
  const antes = f.queCotiza;
  // z7: si el vendedor CAMBIA a Industria con baterías elegidas (o elige baterías siendo Industria), «qué se cotiza» se limpia
  // para que elija otra. Una cotización vieja que ya lo trae no pasa por aquí al abrirse: se queda como estaba.
  const cambiaAlgo = (cambio.clienteTipo !== undefined && cambio.clienteTipo !== f.clienteTipo) || (cambio.queCotiza !== undefined && cambio.queCotiza !== f.queCotiza);
  if (cambiaAlgo && n.queCotiza && !queCotizaOfrecida(n.clienteTipo, n.queCotiza)) n.queCotiza = null;
  if (!n.clienteTipo || !n.queCotiza || !QUE_COTIZA_DISPONIBLE[n.queCotiza]) return { ...n, tipo: null };
  n.tipo = tipoDeEnfoque(n.clienteTipo, n.queCotiza);
  // casa o negocio por el TIPO DE CLIENTE (en baterías también: una casa con baterías no se queda con una tarifa de negocio)
  if (n.clienteTipo === 'casa' && n.tarifa !== 'no_se' && !TARIFAS_CASA.includes(n.tarifa)) n.tarifa = 'no_se';
  if ((n.clienteTipo === 'comercio' || n.clienteTipo === 'industria') && n.tarifa !== 'no_se' && TARIFAS_CASA.includes(n.tarifa)) n.tarifa = 'no_se';
  if (cambio.queCotiza && cambio.queCotiza !== antes) {
    if (n.queCotiza === 'paneles_baterias') {
      n.respaldoTipo = 'hibrido';
      n.respaldoConPaneles = true;
    } else if (n.queCotiza === 'baterias') {
      n.respaldoTipo = 'respaldo';
      n.respaldoConPaneles = false;
    } else if (n.queCotiza === 'aislado') {
      n.respaldoConPaneles = true;
      n.esNuevo = true;
      n.dosMedidores = false;
      // Sin CFE: lo que se haya contestado de la red antes de cambiar a aislado (tarifa, servicio, transformador, facturación)
      // se esconde y ya no aplica; se limpia para que no se guarde (verificación del 14-oct: el resumen decía «Servicio de CFE:
      // Trifásico · 220 V» en un cliente sin CFE y el cálculo usaba las cuotas PDBT).
      n.tarifa = 'no_se';
      n.servicioFases = null;
      n.servicioTension = null;
      n.transformador = null;
      n.facturacion = null;
    }
    if (antes === 'aislado' && n.queCotiza !== 'aislado') n.esNuevo = false;
    // Recorte de punta (z54): con los 12 recibos GDMTH de un medidor (no cliente nuevo, ni dos medidores, ni ampliación de paneles)
    if (n.queCotiza === 'peak_shaving') {
      n.esNuevo = false;
      n.dosMedidores = false;
      if (n.tarifa === 'no_se' || n.tarifa !== 'GDMTH') n.tarifa = 'GDMTH';
      n.facturacion = 'mensual';
    }
    if (antes === 'peak_shaving' && n.queCotiza !== 'peak_shaving') {
      n.recorteNoche = null;
      n.recorteSinPaneles = null;
    }
    // Ampliación de baterías: no hay recibo, tarifa, consumo, medidores ni servicio de CFE (no se preguntan ni se guardan).
    if (n.queCotiza === 'ampliacion_baterias') {
      n.esNuevo = false;
      n.dosMedidores = false;
      n.cambio = 'igual';
      n.tarifa = 'no_se';
      n.servicioFases = null;
      n.servicioTension = null;
      n.transformador = null;
      n.facturacion = null;
      n.regimen = null;
    }
  }
  if (!enfoqueConPaneles(n.queCotiza)) n.ampliacion = false;
  return n;
}

/** ¿Lo que dice la forma todavía no saca PDF? (híbrido o aislado: el MISMO texto con el que el generador lo rechaza,
 *  compartido → pdfTodaviaNoSale). Verificación adversarial del 14-oct: el vendedor se enteraba con un 422 al último clic. */
export function pdfTodaviaNoSaleForma(f: Pick<FormaCuestionario, 'tipo' | 'respaldoTipo' | 'esNuevo'>): string | null {
  return pdfTodaviaNoSale({ tipo: f.tipo, respaldo: f.tipo === 'baterias' ? { tipo: f.respaldoTipo } : null, consumo: { es_nuevo: f.esNuevo === true } });
}

/** ¿Esta tarjeta de «Qué cotizas» lleva a una cotización que todavía no saca PDF? (sale de lo mismo que precarga conEnfoque). */
export function queCotizaSinPdf(q: QueCotiza): string | null {
  if (!QUE_COTIZA_DISPONIBLE[q]) return null;
  return pdfTodaviaNoSaleForma(conEnfoque(formaVacia(), { clienteTipo: 'casa', queCotiza: q }));
}

/** Al salir del primer paso, los casos especiales no marcados quedan en «no» (ya no se vuelven a preguntar más adelante), y
 *  unas baterías sin contestar «¿lleva paneles?» quedan como dice el enfoque (verificación adversarial del 14-oct: un borrador de
 *  antes quedaba con la respuesta en null, escondida, y «Siguiente» no avanzaba ni decía por qué). */
export function cerrarInicio(f: FormaCuestionario): FormaCuestionario {
  const esNuevo = f.queCotiza === 'aislado' ? true : f.tipo === 'ampliacion_baterias' ? false : (f.esNuevo ?? false);
  // Industria nueva: el motor la calcula como GDMTO; que la pantalla lo diga desde el principio (antes lo hacía «No, es nuevo»).
  // Baterías nuevas de un negocio: PDBT (comercio) o GDMTO (industria), por el tipo de cliente elegido (verificación del 14-oct:
  // sin esto se calculaban y salían en el PDF como casa, «tu casa» y 2–3 semanas).
  const nuevaDe = (t: string) => esNuevo && f.tarifa === 'no_se' && (f.tipo === t || (f.tipo === 'baterias' && f.clienteTipo === t));
  const tarifa = nuevaDe('industria') ? 'GDMTO' : f.tipo === 'baterias' && nuevaDe('comercio') ? 'PDBT' : f.tarifa;
  const respaldoConPaneles = f.tipo === 'baterias' && f.respaldoConPaneles === null ? f.queCotiza === 'paneles_baterias' || (!f.queCotiza && f.respaldoTipo === 'hibrido') : f.respaldoConPaneles;
  return { ...f, esNuevo, tarifa, respaldoConPaneles, ampliacion: f.ampliacion ?? false, dosMedidores: esNuevo ? false : (f.dosMedidores ?? false) };
}

/** Un borrador local de ANTES del paso «Qué cotizas» (sin tipo de cliente ni qué se cotiza; el día del despliegue habrá
 *  vendedores a media captura): se deduce el enfoque y, si ya había pasado del primer paso, se cierra como si lo hubiera
 *  cerrado ahí. Verificación adversarial del 14-oct: quedaba en «Situación» con «¿ya tiene recibos?», «¿ya tiene paneles?» y
 *  «¿lleva paneles?» en null, escondidas, y «Siguiente» no avanzaba ni enseñaba ningún error. Sin tipo, vuelve al inicio. */
export function migrarBorrador(f0: FormaCuestionario, paso: Paso): { forma: FormaCuestionario; paso: Paso } {
  // 28-oct: las horas de los equipos del aislado de un borrador de antes (total al día) a «de día» y «de noche»
  const cargas = (f0.respaldoCargas ?? []).map(migrarCargaRespaldo);
  const f = cargas.some((c, i) => c !== f0.respaldoCargas[i]) ? { ...f0, respaldoCargas: cargas } : f0;
  if (f.clienteTipo || f.queCotiza) return { forma: f, paso };
  const enfoque = enfoqueDe(f);
  if (!enfoque.clienteTipo || !enfoque.queCotiza) return { forma: f, paso: 'inicio' };
  const n = { ...f, ...enfoque };
  return { forma: paso === 'inicio' ? n : cerrarInicio(n), paso };
}

export const PASOS = ['inicio', 'cliente', 'situacion', 'notas'] as const;
export type Paso = (typeof PASOS)[number];

export function formaVacia(): FormaCuestionario {
  return {
    nombre: '',
    telefono: '',
    correo: '',
    ciudad: 'Hermosillo',
    estado: 'Sonora',
    giro: '',
    titularEsCliente: null,
    titularNombre: '',
    tipo: null,
    esNuevo: null,
    metodoEstimacion: 'kwh_mes',
    kwhMes: '',
    pagoMonto: '',
    pagoPeriodo: 'bimestral',
    fuenteEstimacion: '',
    cargas: [],
    consumosAutorizados: '',
    cambio: 'igual',
    cambioDetalle: '',
    consumoFuturo: '',
    aumento: null,
    ampliacion: null,
    existentePaneles: '',
    existentePanelW: '',
    existenteKwp: '',
    existenteInversor: '',
    existenteNotas: '',
    existenteInvMarca: '',
    existenteInvModelo: '',
    existenteInvKw: '',
    existenteInvCantidad: '1',
    existenteAnio: '',
    existenteInstalo: null,
    existenteConexion: 'inversor_nuevo',
    dosMedidores: null,
    visita: null,
    facturacion: null,
    tarifa: 'no_se',
    regimen: null,
    tasaIsr: '30',
    estructura: 'losa_lamina',
    servicioFases: null,
    servicioTension: null,
    transformador: null,
    tarifaLeida: null,
    negocioAPdbt: null,
    notas: '',
    respaldoTipo: 'respaldo',
    respaldoConPaneles: null,
    respaldoCargas: [],
    respaldoNotas: '',
    clienteTipo: null,
    queCotiza: null,
    ampBateria: '',
    ampActuales: '',
    ampNuevas: '',
    recorteNoche: null,
    recorteSinPaneles: null,
  };
}

export function cargaRespaldoVacia(): FormaCargaRespaldo {
  return { equipo: '', cantidad: '1', w: '', horas_de_dia: '', horas_noche: '' };
}

/** ¿El cliente de la forma es un negocio? Paneles: comercio o industria; baterías o aislado: el tipo de cliente elegido al
 *  inicio (20-oct-2026: ahí no se preguntaba el régimen y la propuesta salía con el FINSUS de persona). */
export function negocioDeForma(f: Pick<FormaCuestionario, 'tipo' | 'clienteTipo'>): boolean {
  return f.tipo === 'comercio' || f.tipo === 'industria' || (llevaRespaldo(f.tipo) && (f.clienteTipo === 'comercio' || f.clienteTipo === 'industria'));
}

/** ¿Este tipo de proyecto pregunta qué se respalda? (el contrato solo acepta respaldo con baterías o aislado). */
export function llevaRespaldo(tipo: TipoProyecto | null): boolean {
  return tipo === 'baterias' || tipo === 'aislado';
}

export function cargaVacia(): FormaCarga {
  return { equipo: '', cantidad: '1', w: '', horas_dia: '', meses_uso: '12' };
}

const t = (s: string) => s.trim();
const textoONull = (s: string) => (t(s) === '' ? null : t(s));

/** "1,234.5" → 1234.5 · "" → null · "abc" → NaN (el esquema lo rechaza con su mensaje). */
export function numeroDe(s: string): number | null {
  const limpio = s.trim().replace(/[\s,$]/g, '');
  if (limpio === '') return null;
  return Number(limpio);
}

/** ¿El formulario está en media tensión? La tarifa de media tensión (GDMTO/GDMTH) o, con la tarifa en "No sé", la que va a usar
 *  el cotizador: la leída del recibo (al editar) o GDMTO en una industria NUEVA (sin recibo que leer: el motor la calcula como
 *  GDMTO, periodos.ts → tarifaDe). Verificación adversarial de la etapa 2: con "No sé (se calcula como GDMTO)" la industria
 *  nueva se calculaba en media tensión sin la pregunta obligatoria del transformador. */
export function mediaTensionForma(f: Pick<FormaCuestionario, 'tarifa' | 'tipo' | 'esNuevo' | 'tarifaLeida'>): boolean {
  // aislado: sin CFE, nunca media tensión (la pregunta del transformador no se enseña: no puede quedar exigida)
  if (f.tipo === 'aislado') return false;
  if (f.tarifa !== 'no_se') return esMediaTension(f.tarifa);
  return esMediaTension(f.tarifaLeida ?? null) || (f.tipo === 'industria' && f.esNuevo === true);
}

/** La respuesta del transformador que trae un cuestionario guardado EN MEDIA TENSIÓN (trifásico con su tensión, o sin tensión =
 *  «No sé: lo veo en la visita»). Una respuesta de baja tensión (monofásico, bifásico, o un trifásico de una tarifa de baja
 *  tensión) no cuenta: si la tarifa pasa a media tensión, hay que contestar. */
function transformadorDe(c: Cuestionario, f: FormaCuestionario): FormaCuestionario['transformador'] {
  return mediaTensionForma(f) && c.servicio?.fases === 'trifasico' ? (c.servicio.tension ?? 'visita') : null;
}

export function formaDesde(c: Cuestionario, extra: { tarifaLeida?: Tarifa | null; negocioAPdbt?: boolean | null } = {}): FormaCuestionario {
  const f = formaDesdeSinTransformador(c, extra);
  return { ...f, ...enfoqueDe(f), transformador: transformadorDe(c, f) };
}

function formaDesdeSinTransformador(c: Cuestionario, extra: { tarifaLeida?: Tarifa | null; negocioAPdbt?: boolean | null }): FormaCuestionario {
  const e = c.ampliacion.existente;
  const s = (n: number | null | undefined) => (n === null || n === undefined ? '' : String(n));
  return {
    nombre: c.cliente.nombre,
    telefono: c.cliente.telefono ?? '',
    correo: c.cliente.correo ?? '',
    ciudad: c.cliente.ciudad,
    estado: c.cliente.estado,
    giro: c.cliente.giro ?? '',
    titularEsCliente: c.titular.es_el_cliente,
    titularNombre: c.titular.nombre ?? '',
    tipo: c.tipo,
    esNuevo: c.consumo.es_nuevo,
    metodoEstimacion: c.estimacion?.metodo ?? 'kwh_mes',
    kwhMes: s(c.estimacion?.kwh_mes),
    pagoMonto: c.estimacion?.pago ? String(c.estimacion.pago.monto) : '',
    pagoPeriodo: c.estimacion?.pago?.periodo ?? 'bimestral',
    fuenteEstimacion: c.estimacion?.fuente ?? '',
    cargas: (c.estimacion?.cargas ?? []).map((x) => ({ equipo: x.equipo, cantidad: s(x.cantidad), w: s(x.w), horas_dia: s(x.horas_dia), meses_uso: s(x.meses_uso) })),
    // lo guardado ya estaba autorizado: solo se vuelve a pedir si el vendedor lo cambia
    consumosAutorizados: firmaConsumos(c.tipo, (c.estimacion?.cargas ?? []).map((x) => ({ equipo: x.equipo, cantidad: s(x.cantidad), w: s(x.w), horas_dia: s(x.horas_dia), meses_uso: s(x.meses_uso) }))),
    cambio: c.consumo.cambio,
    cambioDetalle: c.consumo.cambio_detalle ?? '',
    consumoFuturo: s(c.consumo.consumo_futuro_kwh_anual),
    aumento: aumentoSinDecision(c.consumo.aumento ?? null),
    ampliacion: c.ampliacion.es_ampliacion,
    existentePaneles: s(e?.paneles),
    existentePanelW: s(e?.panel_w),
    existenteKwp: s(e?.kwp),
    existenteInversor: e?.inversor ?? '',
    existenteNotas: e?.notas ?? '',
    existenteInvMarca: e?.inversores?.[0]?.marca ?? '',
    existenteInvModelo: e?.inversores?.[0]?.modelo ?? '',
    existenteInvKw: s(e?.inversores?.[0]?.kw),
    existenteInvCantidad: e?.inversores?.[0] ? s(e.inversores[0].cantidad) : '1',
    existenteAnio: s(e?.anio_instalacion),
    existenteInstalo: e?.instalo ?? null,
    existenteConexion: e?.conexion ?? 'inversor_nuevo',
    dosMedidores: c.dos_medidores,
    visita: c.visita_tecnica_hecha,
    facturacion: c.facturacion,
    tarifa: c.tarifa,
    regimen: c.fiscal?.regimen ?? null,
    tasaIsr: String(c.fiscal?.tasa_isr_pct ?? 30),
    estructura: c.estructura ?? 'losa_lamina',
    servicioFases: c.servicio?.fases ?? null,
    servicioTension: c.servicio?.tension ?? null,
    transformador: null,
    tarifaLeida: extra.tarifaLeida ?? null,
    negocioAPdbt: extra.negocioAPdbt ?? null,
    notas: c.notas_libres,
    respaldoTipo: c.respaldo?.tipo === 'hibrido' ? 'hibrido' : 'respaldo',
    respaldoConPaneles: c.respaldo ? c.respaldo.con_paneles : null,
    respaldoCargas: (c.respaldo?.cargas ?? []).map((x) => ({ equipo: x.equipo, cantidad: s(x.cantidad), w: s(x.w), ...formaHorasDe(x) })),
    respaldoNotas: c.respaldo?.notas ?? '',
    // baterías o aislado: el que se guardó (enfoqueDe lo completa; en paneles sale del tipo)
    clienteTipo: c.tipo === 'baterias' || c.tipo === 'aislado' || c.tipo === 'ampliacion_baterias' ? (c.cliente.tipo ?? null) : null,
    queCotiza: null,
    ampBateria: '',
    ampActuales: '',
    ampNuevas: '',
    recorteNoche: c.recorte_punta ? true : null,
    recorteSinPaneles: c.recorte_punta?.sin_paneles ?? null,
  };
}

/** ¿La obra lleva paneles? Todo lo que no es baterías, sí (también el aislado: verificación adversarial del 14-oct, ya no se
 *  pregunta; sin red y sin paneles no hay con qué recargar las baterías); baterías, según «¿lleva paneles?» (sin contestar: lo
 *  mismo que manda respaldoDe, el híbrido con paneles). Decide si se pregunta y se manda la estructura. */
export function llevaPaneles(f: Pick<FormaCuestionario, 'tipo' | 'respaldoConPaneles' | 'respaldoTipo' | 'respaldoCargas'> & Partial<Pick<FormaCuestionario, 'queCotiza'>>): boolean {
  // recorte de punta (z54): la batería sola, sin paneles nuevos (verificación de hoy70: preguntaba «¿Sobre qué van los paneles?»)
  if (f.queCotiza === 'peak_shaving') return false;
  if (f.tipo !== 'baterias') return true;
  return f.respaldoConPaneles ?? f.respaldoTipo === 'hibrido';
}

/** ¿Se manda cuestionario.respaldo? Diego, 11-oct: el respaldo y el híbrido son de TODA la casa (se calculan con el recibo,
 *  sin equipos): siempre van. El aislado (casi nunca) va por equipos: solo con al menos uno. */
export function mandaRespaldo(f: Pick<FormaCuestionario, 'tipo' | 'respaldoCargas'>): boolean {
  return f.tipo === 'baterias' || (f.tipo === 'aislado' && f.respaldoCargas.length > 0);
}

/** Lo que va en cuestionario.respaldo (null si no va). El aislado va con tipo "aislado" (el esquema lo exige); el respaldo y el
 *  híbrido, sin equipos (toda la casa). */
function respaldoDe(f: FormaCuestionario): unknown {
  if (!mandaRespaldo(f)) return null;
  const r: Record<keyof RespaldoCuestionario, unknown> = {
    tipo: f.tipo === 'aislado' ? 'aislado' : f.respaldoTipo,
    // el aislado siempre con paneles (no se pregunta; una forma de antes con «Solo baterías» también va con paneles)
    con_paneles: f.tipo === 'aislado' ? true : (f.respaldoConPaneles ?? f.respaldoTipo === 'hibrido'),
    // Las horas solo si el vendedor escribió alguna (las dos vacías = las de omisión del equipo; no se inventan claves).
    cargas:
      f.tipo === 'aislado'
        ? f.respaldoCargas.map((x) => ({
            equipo: t(x.equipo),
            cantidad: numeroDe(x.cantidad),
            w: numeroDe(x.w),
            ...(horasAisladoDe(x) ?? {}),
          }))
        : [],
    notas: textoONull(f.respaldoNotas),
  };
  return r;
}

/** Lo que el formulario manda (sin validar todavía). */
/** z46-2 (P22): la estimación por lo que paga. Sin monto válido va `pago: null` y el esquema lo marca en «pagoMonto». */
function pagoEstimacionDe(f: FormaCuestionario) {
  const monto = numeroDe(f.pagoMonto);
  const pago = monto !== null && monto > 0 ? { monto, periodo: f.pagoPeriodo } : null;
  return { metodo: 'pago' as const, cargas: [], kwh_mes: null, pago, fuente: pago ? fuenteDePago(pago) : 'Lo que paga a CFE' };
}

export function borradorCuestionario(f: FormaCuestionario): unknown {
  // Ampliación de baterías: el cuestionario mínimo (cliente, para quién es, si ya se hizo la visita y las notas).
  if (f.tipo === 'ampliacion_baterias')
    return cuestionarioAmpliacion(
      {
        nombre: t(f.nombre),
        telefono: textoONull(f.telefono.replace(/[\s()-]/g, '')),
        correo: textoONull(f.correo),
        ciudad: t(f.ciudad),
        estado: t(f.estado),
        giro: null,
        ...(f.clienteTipo ? { tipo: f.clienteTipo } : {}),
      },
      { visita_tecnica_hecha: f.visita ?? false, notas_libres: f.notas },
    );
  const cargas = f.cargas.map((x) => ({
    equipo: t(x.equipo),
    cantidad: numeroDe(x.cantidad),
    w: numeroDe(x.w),
    horas_dia: numeroDe(x.horas_dia),
    meses_uso: numeroDe(x.meses_uso),
  }));
  return {
    cliente: {
      nombre: t(f.nombre),
      telefono: textoONull(f.telefono.replace(/[\s()-]/g, '')),
      correo: textoONull(f.correo),
      ciudad: t(f.ciudad),
      estado: t(f.estado),
      // Baterías o aislado de un negocio: el giro va si se escribió (el esquema lo deja).
      giro: f.tipo === 'comercio' || f.tipo === 'industria' || (llevaRespaldo(f.tipo) && (f.clienteTipo === 'comercio' || f.clienteTipo === 'industria')) ? textoONull(f.giro) : null,
      // Baterías o aislado: para quién es (casa, comercio o industria) se GUARDA, y al editar vuelve igual (en paneles lo dice
      // el tipo del proyecto y la clave no va: un cuestionario de paneles va y vuelve como antes).
      ...(llevaRespaldo(f.tipo) && f.clienteTipo ? { tipo: f.clienteTipo } : {}),
    },
    // aislado: no hay recibo de CFE (la pregunta no se hace): a nombre del cliente
    titular: f.tipo === 'aislado' ? { es_el_cliente: true, nombre: null } : { es_el_cliente: f.titularEsCliente ?? true, nombre: f.titularEsCliente === false ? textoONull(f.titularNombre) : null },
    tipo: f.tipo,
    consumo: {
      es_nuevo: f.esNuevo ?? false,
      cambio: f.cambio,
      cambio_detalle: f.cambio === 'igual' ? null : textoONull(f.cambioDetalle),
      // ADENDA z52-2: con el aumento aceptado, el consumo futuro de antes ya no va (son excluyentes: el esquema lo exige)
      consumo_futuro_kwh_anual: f.cambio === 'igual' || (f.aumento && aumentoEnForma(f)) ? null : numeroDe(f.consumoFuturo),
      // bloqueo del verificador (29-oct): con lo que DECIDIÓ esta pantalla al guardar (el motor lo compara con lo que calcula)
      ...(f.aumento && decisionDeForma(f) ? { aumento: { ...f.aumento, pantalla: decisionDeForma(f)! } } : {}),
    },
    estimacion: f.esNuevo
      ? f.metodoEstimacion === 'cuadro_cargas' && !cuadroConCurva(f.tipo)
        ? // z58: baterías (híbrido) y aislado: el motor no reparte el cuadro con la curva; se manda el promedio de la tabla y el
          // motor lo reparte con la curva de casas de Sonora (la MISMA que enseña la pantalla: logica/consumos-ia.ts).
          { metodo: 'kwh_mes', cargas: [], kwh_mes: promedioDeCargas(f.tipo, f.cargas) || null, fuente: t(f.fuenteEstimacion) }
        : f.metodoEstimacion === 'pago'
          ? // z46-2 (P22): el motor saca los kWh de lo que paga; la fuente es la frase de «estimado» (no se le pide al vendedor).
            pagoEstimacionDe(f)
          : {
          metodo: f.metodoEstimacion,
          cargas: f.metodoEstimacion === 'cuadro_cargas' ? cargas : [],
          kwh_mes: f.metodoEstimacion === 'cuadro_cargas' ? null : numeroDe(f.kwhMes),
          fuente: t(f.fuenteEstimacion),
        }
      : null,
    visita_tecnica_hecha: f.visita ?? false,
    ampliacion: {
      es_ampliacion: f.ampliacion ?? false,
      existente: f.ampliacion
        ? {
            paneles: numeroDe(f.existentePaneles),
            panel_w: numeroDe(f.existentePanelW),
            kwp: numeroDe(f.existenteKwp),
            inversor: textoONull(f.existenteInversor),
            notas: textoONull(f.existenteNotas),
            ...existenteEstructurado(f),
          }
        : null,
    },
    // aislado: sin CFE (ni facturación, ni tarifa, ni servicio: la pantalla no los enseña)
    facturacion: f.tipo === 'aislado' ? 'no_se' : (f.facturacion ?? 'no_se'),
    dos_medidores: f.dosMedidores ?? false,
    tarifa: f.tipo === 'aislado' ? 'no_se' : f.tarifa,
    notas_libres: f.notas,
    // Sin respaldo, la clave ni se manda (como un cuestionario de antes del contrato v2): la ida y vuelta no inventa
    // un "respaldo: null" que el original no traía.
    ...(mandaRespaldo(f) ? { respaldo: respaldoDe(f) } : {}),
    // Diego, 12-oct: régimen fiscal (solo negocio). Revisión de ventas, propuesta 5: en negocio es obligatorio (faltantes lo
    // pide en el paso "tipo"; «No sé todavía» es una respuesta); sin elegir, la clave no se manda y el servidor no la crea.
    // Persona moral: 30 %; persona física con actividad empresarial: la suya (por omisión 30 %).
    // 20-oct-2026: también baterías o aislado de un negocio (sin régimen salían con el FINSUS de persona; z21).
    ...(negocioDeForma(f) && f.regimen
      ? { fiscal: { regimen: f.regimen, tasa_isr_pct: f.regimen === 'pf_empresarial' ? numeroDe(f.tasaIsr) : 30 } }
      : {}),
    // Recorte de punta (z54): sus 2 respuestas. «No trabaja de noche» y «Le caben paneles» van tal cual y el esquema las rechaza
    // (faltantes dice antes por qué, con el mensaje de Diego); sin la tarjeta, la clave no va (como un cuestionario de antes).
    ...(f.queCotiza === 'peak_shaving' || (f.tipo === 'industria' && f.recorteSinPaneles)
      ? { recorte_punta: { trabaja_de_noche: f.recorteNoche, sin_paneles: f.recorteSinPaneles } }
      : {}),
    // Diego, 12-oct: la estructura solo si no es la de siempre (sin la clave = losa/lámina, como un cuestionario de antes) y
    // el servicio solo si se contestaron las fases (si no, el motor lo deduce y pide confirmarlo en la visita).
    // Baterías SIN paneles: el selector se oculta y la estructura no se manda aunque haya quedado puesta (hallazgo del 12-oct:
    // el PDF y el resumen decían «Carport» en una cotización sin paneles).
    ...(f.estructura !== 'losa_lamina' && llevaPaneles(f) ? { estructura: f.estructura } : {}),
    // En media tensión el servicio sale de la respuesta del transformador (trifásico; «No sé: lo veo en la visita» = sin tensión).
    ...(mediaTensionForma(f) && f.transformador
      ? { servicio: { fases: 'trifasico', tension: f.transformador === 'visita' ? null : f.transformador } }
      : f.servicioFases && f.tipo !== 'aislado'
        ? { servicio: { fases: f.servicioFases, tension: f.servicioTension } }
        : {}),
  };
}

/** Lo estructurado del sistema que ya tiene (variante "ampliación"). Solo se manda lo que el vendedor capturó: un
 *  borrador de antes (sin estos campos) va y vuelve igual. */
function existenteEstructurado(f: FormaCuestionario): Partial<NonNullable<Cuestionario['ampliacion']['existente']>> {
  const out: Partial<NonNullable<Cuestionario['ampliacion']['existente']>> = {};
  if (f.existenteInvMarca.trim() || f.existenteInvKw.trim())
    out.inversores = [{ marca: f.existenteInvMarca.trim(), modelo: textoONull(f.existenteInvModelo), kw: numeroDe(f.existenteInvKw) as number, cantidad: numeroDe(f.existenteInvCantidad) ?? 1 }];
  if (f.existenteAnio.trim()) out.anio_instalacion = numeroDe(f.existenteAnio) as number;
  if (f.existenteInstalo) out.instalo = f.existenteInstalo;
  if (f.existenteConexion === 'inversor_actual') out.conexion = 'inversor_actual';
  return out;
}

export type ErroresForma = Partial<Record<string, string>>;

/** Traduce la ruta de un error del esquema al campo del formulario. */
function campoDe(ruta: (string | number | symbol)[]): string {
  const p = ruta.map(String).join('.');
  const tabla: Record<string, string> = {
    'cliente.nombre': 'nombre',
    'cliente.telefono': 'telefono',
    'cliente.correo': 'correo',
    'cliente.ciudad': 'ciudad',
    'cliente.estado': 'estado',
    'cliente.giro': 'giro',
    'fiscal.tasa_isr_pct': 'tasaIsr',
    fiscal: 'regimen',
    'fiscal.regimen': 'regimen',
    'servicio.tension': 'servicioTension',
    'servicio.fases': 'servicioFases',
    estructura: 'estructura',
    'titular.nombre': 'titularNombre',
    tipo: 'tipo',
    'consumo.cambio_detalle': 'cambioDetalle',
    'consumo.consumo_futuro_kwh_anual': 'consumoFuturo',
    'consumo.aumento': 'aumento',
    estimacion: 'estimacion',
    'estimacion.kwh_mes': 'kwhMes',
    'estimacion.pago': 'pagoMonto',
    'estimacion.pago.monto': 'pagoMonto',
    'estimacion.fuente': 'fuenteEstimacion',
    'estimacion.cargas': 'cargas',
    'ampliacion.existente': 'existente',
    'ampliacion.existente.paneles': 'existentePaneles',
    'ampliacion.existente.panel_w': 'existentePanelW',
    'ampliacion.existente.kwp': 'existenteKwp',
    'ampliacion.existente.inversor': 'existenteInversor',
    'ampliacion.existente.notas': 'existenteNotas',
    'ampliacion.existente.inversores': 'existenteInvMarca',
    'ampliacion.existente.anio_instalacion': 'existenteAnio',
    notas_libres: 'notas',
    recorte_punta: 'recorteNoche',
    'recorte_punta.trabaja_de_noche': 'recorteNoche',
    'recorte_punta.sin_paneles': 'recorteSinPaneles',
  };
  if (tabla[p]) return tabla[p]!;
  if (p.startsWith('estimacion.cargas.')) return `carga.${ruta[2] as number}`;
  // las horas de un equipo del aislado se marcan en SU campo (verificación adversarial del 14-oct: «noche > día» salía bajo
  // «Equipo»); lo demás del renglón, en «Equipo»
  // (28-oct: el total del esquema, horas_dia, es día + noche: se marca en «Horas de día», campo horas_de_dia)
  if (p.startsWith('respaldo.cargas.'))
    return ruta[3] === 'horas_dia' ? `cargaRespaldo.${ruta[2] as number}.horas_de_dia` : ruta[3] === 'horas_noche' ? `cargaRespaldo.${ruta[2] as number}.horas_noche` : `cargaRespaldo.${ruta[2] as number}`;
  if (p === 'respaldo.notas') return 'respaldoNotas';
  if (p.startsWith('respaldo')) return 'respaldo';
  return p || 'general';
}

const MENSAJES_CAMPO: Record<string, string> = {
  nombre: 'Escribe el nombre del cliente (de 2 a 60 letras; así sale en el PDF).',
  telefono: 'El teléfono lleva 10 dígitos, sin lada internacional.',
  correo: 'Ese correo no es válido.',
  ciudad: 'Escribe la ciudad.',
  estado: 'Escribe el estado.',
  giro: 'Escribe el giro del negocio (de 2 a 40 letras).',
  tasaIsr: 'La tasa de ISR va de 1 a 40 %.',
  titularNombre: 'Escribe a nombre de quién está el recibo.',
  kwhMes: 'Escribe los kWh al mes (un número mayor que cero).',
  pagoMonto: 'Escribe cuánto paga a CFE con IVA (un número mayor que cero).',
  fuenteEstimacion: 'Di de dónde sale la estimación (de 2 a 300 letras).',
  consumoFuturo: 'Escribe el consumo al año en kWh, o déjalo vacío.',
  existentePaneles: 'Número de paneles entero.',
  existentePanelW: 'Watts por panel (un número).',
  existenteKwp: 'kWp del sistema actual (un número).',
  existenteInvMarca: 'Escribe la marca y los kW del inversor que ya tiene.',
  existenteAnio: 'El año va con 4 cifras (ej. 2021).',
  respaldoNotas: 'Máximo 500 letras.',
};

/** Errores de los sí/no sin contestar, por paso. */
function faltantes(f: FormaCuestionario, paso: Paso, conPiezas: boolean): ErroresForma {
  const e: ErroresForma = {};
  if (paso === 'inicio') {
    if (!f.clienteTipo) e.clienteTipo = 'Elige el tipo de cliente.';
    if (!f.queCotiza) e.queCotiza = 'Elige qué se cotiza.';
    else if (!QUE_COTIZA_DISPONIBLE[f.queCotiza]) e.queCotiza = 'Esa opción todavía no se puede cotizar: elige otra.';
    // Sin tipo de cliente no hay tipo de proyecto: lo que falta es PARA QUIÉN es (verificación del 27-oct: bajo «Ampliación de
    // baterías» ya marcada decía «Elige el tipo de proyecto.»). La ampliación es de casa o comercio (z7: industria no).
    // Recorte de punta (z54-1): solo la industria que trabaja de noche y no puede (o ya tiene) paneles
    else if (f.queCotiza === 'peak_shaving') {
      if (f.recorteNoche === null) e.recorteNoche = 'Contesta si trabaja entre semana de 6 a 10 de la noche.';
      else if (f.recorteNoche === false) e.recorteNoche = MENSAJE_RECORTE_SIN_NOCHE;
      if (f.recorteSinPaneles === null) e.recorteSinPaneles = 'Contesta por qué va sin paneles nuevos.';
      else if (f.recorteSinPaneles === 'le_caben') e.recorteSinPaneles = MENSAJE_RECORTE_LE_CABEN;
    }
    if (paso === 'inicio' && f.queCotiza && QUE_COTIZA_DISPONIBLE[f.queCotiza] && !f.tipo)
      e.tipo = !f.clienteTipo ? (f.queCotiza === 'ampliacion_baterias' ? '¿Para quién es? Casa o comercio: elígelo arriba.' : '¿Para quién es? Elige arriba el tipo de cliente.') : 'Elige el tipo de proyecto.';
  }
  if (paso === 'cliente' && f.titularEsCliente === null && f.tipo !== 'aislado' && f.tipo !== 'ampliacion_baterias') e.titularEsCliente = 'Contesta a nombre de quién está el recibo.';
  // Revisión de ventas, propuesta 5 (Diego, 12-oct noche): el régimen fiscal es obligatorio en comercio e industria
  // 20-oct-2026: también en baterías o aislado de un negocio (el régimen decide el producto de FINSUS, z21).
  if (paso === 'cliente' && negocioDeForma(f) && !f.regimen) e.regimen = 'Elige el régimen fiscal del cliente (o «No sé todavía»).';
  if (paso === 'situacion' && f.tipo === 'ampliacion_baterias') {
    if (f.visita === null) e.visita = 'Contesta si ya se hizo la visita técnica.';
    if (conPiezas) {
      if (!f.ampBateria) e.ampBateria = 'Elige la batería que ya tiene (las nuevas son del mismo modelo).';
      const a = numeroDe(f.ampActuales);
      const n = numeroDe(f.ampNuevas);
      if (a === null || !Number.isInteger(a) || a < 1 || a > 64) e.ampActuales = 'Cuántas baterías ya tiene: un número entero (1 o más).';
      if (n === null || !Number.isInteger(n) || n < 1 || n > 64) e.ampNuevas = 'Cuántas se suman: un número entero (1 o más).';
    }
    return e;
  }
  if (paso === 'situacion') {
    if (f.esNuevo === null) e.esNuevo = 'Contesta si ya tiene recibos de CFE.';
    if (f.ampliacion === null) e.ampliacion = 'Contesta si ya tiene paneles.';
    if (f.dosMedidores === null && f.esNuevo === false) e.dosMedidores = 'Contesta cuántos medidores tiene.';
    if (f.visita === null) e.visita = 'Contesta si ya se hizo la visita técnica.';
    if (f.facturacion === null && f.esNuevo === false) e.facturacion = 'Elige cada cuánto le llega el recibo (o "No sé").';
    if (f.esNuevo && f.metodoEstimacion === 'cuadro_cargas' && f.cargas.length === 0) e.cargas = 'Toca «Calcular consumos con IA» (o usa «kWh al mes» si ya trae un número).';
    // z58: lo que calculó la IA (y lo que ajustó el vendedor) no pasa sin «Usar estos consumos»; si cambia después, se vuelve a pedir.
    else if (f.esNuevo && f.metodoEstimacion === 'cuadro_cargas' && f.consumosAutorizados !== firmaConsumos(f.tipo, f.cargas))
      e.cargas = 'Falta autorizar los consumos: revisa la tabla y toca «Usar estos consumos».';
    // El contrato deja kwh_mes en null (puede venir del cuadro de cargas), pero sin ninguno de los dos no hay qué calcular.
    if (f.esNuevo && f.metodoEstimacion !== 'cuadro_cargas' && f.metodoEstimacion !== 'pago' && !f.kwhMes.trim()) e.kwhMes = 'Escribe los kWh al mes que estimas.';
    if (f.esNuevo && f.metodoEstimacion === 'pago' && !(numeroDe(f.pagoMonto) && numeroDe(f.pagoMonto)! > 0)) e.pagoMonto = 'Escribe cuánto paga a CFE con IVA (como viene en su recibo).';
    if (f.ampliacion && !f.existentePaneles.trim() && !f.existenteKwp.trim()) e.existente = 'Escribe cuántos paneles o cuántos kWp tiene ya.';
    if (f.ampliacion && (f.existenteInvMarca.trim() !== '') !== (f.existenteInvKw.trim() !== '')) e.existenteInvMarca = 'Del inversor que ya tiene escribe la marca y los kW (los dos).';
    if (f.ampliacion && f.existenteConexion === 'inversor_actual' && (!f.existenteInvMarca.trim() || !f.existenteInvKw.trim()))
      e.existenteInvMarca = 'Si los paneles nuevos van a su inversor actual, escribe cuál es (marca y kW).';
    if (mandaRespaldo(f) && f.tipo !== 'aislado' && f.respaldoConPaneles === null) e.respaldoConPaneles = 'Contesta si lleva paneles.';
    // Diego, 12-oct tarde (ADENDA): en media tensión la tensión del transformador es OBLIGATORIA (o «No sé: lo veo en la visita»)
    // (la respuesta propia de la pregunta, no un «Trifásico» de la pregunta de baja tensión; también con "No sé" en la tarifa
    // cuando el cotizador la calcula en media tensión: mediaTensionForma)
    if (mediaTensionForma(f) && f.transformador === null)
      e.servicioTension = `Media tensión: elige la tensión del transformador (220 o 440 V) o «${ETIQUETA_NO_SE_TRANSFORMADOR}».`;
  }
  return e;
}

const CAMPOS_PASO: Record<Paso, readonly string[]> = {
  inicio: ['clienteTipo', 'queCotiza', 'tipo', 'recorteNoche', 'recorteSinPaneles'],
  cliente: ['nombre', 'telefono', 'correo', 'ciudad', 'estado', 'titularNombre', 'titularEsCliente', 'giro', 'regimen', 'tasaIsr'],
  situacion: [
    'esNuevo',
    'estimacion',
    'kwhMes',
    'pagoMonto',
    'fuenteEstimacion',
    'cargas',
    'cambioDetalle',
    'consumoFuturo',
    'aumento',
    'ampliacion',
    'existente',
    'existentePaneles',
    'existentePanelW',
    'existenteKwp',
    'existenteInversor',
    'existenteNotas',
    'dosMedidores',
    'visita',
    'facturacion',
    'respaldo',
    'respaldoConPaneles',
    'respaldoNotas',
    'estructura',
    'servicioFases',
    'servicioTension',
    'ampBateria',
    'ampActuales',
    'ampNuevas',
  ],
  notas: ['notas', 'general'],
};

export function pasoDeCampo(campo: string): Paso {
  if (campo.startsWith('carga.') || campo.startsWith('cargaRespaldo.')) return 'situacion';
  for (const p of PASOS) if (CAMPOS_PASO[p].includes(campo)) return p;
  return 'notas';
}

/** Valida hasta el paso dado (incluido). Devuelve el Cuestionario si TODO está bien. `conPiezas` (ampliación de baterías): pide
 *  también la batería y las cantidades con que nace la cotización (al editar el cuestionario no: esas se cambian en su pantalla). */
export function validar(f: FormaCuestionario, hasta: Paso, conPiezas = true): { errores: ErroresForma; cuestionario: Cuestionario | null } {
  const pasos = PASOS.slice(0, PASOS.indexOf(hasta) + 1);
  const errores: ErroresForma = {};
  for (const p of pasos) Object.assign(errores, faltantes(f, p, conPiezas));
  if (pasos.includes('situacion')) Object.assign(errores, erroresHorasAislado(f));
  const r = Cuestionario.safeParse(borradorCuestionario(f));
  if (!r.success) {
    for (const i of r.error.issues) {
      const campo = campoDe(i.path);
      if (!pasos.includes(pasoDeCampo(campo))) continue;
      if (errores[campo]) continue;
      // las horas de un equipo ya marcadas aquí (erroresHorasAislado): el del esquema diría lo mismo con el total
      if (/\.horas_(de_dia|noche)$/.test(campo) && (errores[campo.replace(/horas_\w+$/, 'horas_de_dia')] || errores[campo.replace(/horas_\w+$/, 'horas_noche')])) continue;
      errores[campo] =
        MENSAJES_CAMPO[campo] ??
        (campo.startsWith('cargaRespaldo.')
          ? /\.horas_(de_dia|noche)$/.test(campo)
            ? i.code === 'custom'
              ? i.message
              : `Revisa las horas de este equipo: de día, de 0 a ${24 - HORAS_NOCHE_MIN}; de noche, de 0 a ${HORAS_NOCHE_MAX} (o déjalas vacías: las típicas del equipo).`
            : 'Revisa este equipo: nombre (2 a 60 letras), cuántos (entero) y watts (mayor que 0).'
          : campo.startsWith('carga.')
            ? 'Revisa este equipo: nombre, cuántos, watts, horas al día y meses al año.'
            : i.message);
    }
  }
  const ok = Object.keys(errores).length === 0 && r.success && hasta === 'notas';
  return { errores, cuestionario: ok && r.success ? r.data : null };
}

/** Recibos que se van a pedir por medidor con lo contestado hasta ahora (0 = cliente nuevo). */
export function recibosPorMedidor(f: FormaCuestionario): number {
  return recibosEsperados({
    tarifa: f.tarifa,
    consumo: { es_nuevo: f.esNuevo ?? false, cambio: 'igual', cambio_detalle: null, consumo_futuro_kwh_anual: null },
  });
}

const r2 = (h: number) => Math.round(h * 100) / 100;

/** Las horas TÍPICAS de un equipo del aislado partidas en día y noche: las que usa el motor cuando no se escribe ninguna
 *  (usoDeCarga, la MISMA regla: un equipo de 24 h trabaja 14 de noche). */
function tipicasDiaNoche(equipo: string): { dia: number; noche: number } {
  const u = usoDeCarga({ equipo });
  return { dia: r2(Math.max(0, u.horas_dia - u.horas_noche)), noche: r2(u.horas_noche) };
}

/** Lo que se ve en gris en «Horas de día» y «Horas de noche» de un equipo del aislado: las típicas del equipo (28-oct: las dos
 *  son independientes; la que se deja vacía toma la típica). Sin decimales de más («10», no «10.00»). */
export function horasEnGris(c: Pick<FormaCargaRespaldo, 'equipo'>): { dia: string; noche: string } {
  const t = tipicasDiaNoche(c.equipo);
  return { dia: String(t.dia), noche: String(t.noche) };
}

/** Las horas que van al servidor (mismo contrato de antes): horas_dia = día + noche y horas_noche = noche. Si el vendedor
 *  escribió solo una, la otra es la típica (la que se ve en gris), y van las dos: así el motor calcula lo que se ve. null = las
 *  dos vacías (no se manda nada: las de omisión). Un número mal escrito pasa como NaN y el esquema lo rechaza. */
export function horasAisladoDe(c: Pick<FormaCargaRespaldo, 'equipo' | 'horas_de_dia' | 'horas_noche'>): { horas_dia: number; horas_noche: number } | null {
  const d = (c.horas_de_dia ?? '').trim();
  const n = (c.horas_noche ?? '').trim();
  if (!d && !n) return null;
  const t = tipicasDiaNoche(c.equipo);
  const dia = d ? (numeroDe(d) ?? Number.NaN) : t.dia;
  const noche = n ? (numeroDe(n) ?? Number.NaN) : t.noche;
  return { horas_dia: r2(dia + noche), horas_noche: noche };
}

/** Al revés (una cotización guardada o un borrador de antes): del total y la noche a «de día» y «de noche». Con las horas que
 *  calculó el motor (usoDeCarga): si se guardó solo una, las dos quedan escritas y al volver a guardar el precio no cambia. */
export function formaHorasDe(x: { equipo: string; horas_dia?: number | null; horas_noche?: number | null }): Pick<FormaCargaRespaldo, 'horas_de_dia' | 'horas_noche'> {
  if (x.horas_dia == null && x.horas_noche == null) return { horas_de_dia: '', horas_noche: '' };
  const u = usoDeCarga(x);
  return { horas_de_dia: String(r2(Math.max(0, u.horas_dia - u.horas_noche))), horas_noche: String(r2(u.horas_noche)) };
}

/** Un equipo de un borrador de antes del 28-oct (horas_dia = TOTAL al día) en la forma de hoy; uno de hoy, tal cual. */
export function migrarCargaRespaldo(x: FormaCargaRespaldo & { horas_dia?: string }): FormaCargaRespaldo {
  if (!('horas_dia' in x) || 'horas_de_dia' in x) return x;
  const { horas_dia, ...resto } = x;
  const num = (v: string | undefined) => {
    const h = numeroDe(v ?? '');
    return h !== null && Number.isFinite(h) && h >= 0 ? h : null;
  };
  const total = num(horas_dia);
  const noche = num(resto.horas_noche);
  return { ...resto, ...formaHorasDe({ equipo: resto.equipo, horas_dia: total !== null && total > 0 ? total : null, horas_noche: noche }) };
}

/** Las horas de cada equipo del aislado, con su mensaje en SU campo (28-oct). El esquema revisa el total; esto dice qué
 *  escribir en día y noche. */
function erroresHorasAislado(f: Pick<FormaCuestionario, 'tipo' | 'respaldoCargas'>): ErroresForma {
  const e: ErroresForma = {};
  if (f.tipo !== 'aislado') return e;
  const sol = 24 - HORAS_NOCHE_MIN;
  f.respaldoCargas.forEach((c, i) => {
    const h = horasAisladoDe(c);
    if (!h) return;
    const nombre = c.equipo.trim() ? `${c.equipo.trim()}: ` : '';
    const escrita = (v: string | undefined) => ((v ?? '').trim() ? (numeroDe(v!) ?? Number.NaN) : null);
    const dia = escrita(c.horas_de_dia);
    const noche = escrita(c.horas_noche);
    const malDia = dia !== null && !(Number.isFinite(dia) && dia >= 0 && dia <= sol);
    const malNoche = noche !== null && !(Number.isFinite(noche) && noche >= 0 && noche <= HORAS_NOCHE_MAX);
    if (malDia) e[`cargaRespaldo.${i}.horas_de_dia`] = `${nombre}de día, de 0 a ${sol} h (el día más largo en Sonora tiene unas ${sol} h de sol; lo demás va en «Horas de noche»).`;
    if (malNoche) e[`cargaRespaldo.${i}.horas_noche`] = `${nombre}de noche, de 0 a ${HORAS_NOCHE_MAX} h (la noche más larga en Sonora).`;
    if (malDia || malNoche) return;
    if (h.horas_dia > 24 + 1e-9) e[`cargaRespaldo.${i}.horas_de_dia`] = `${nombre}de día y de noche suman ${r2(h.horas_dia)} h; un día tiene 24.`;
    else if (h.horas_dia <= 0) e[`cargaRespaldo.${i}.horas_de_dia`] = `${nombre}no se usa ni de día ni de noche: pon sus horas, déjalas vacías (las típicas) o quita el equipo.`;
  });
  return e;
}

/** AISLADO (verificación adversarial del 14-oct): los kWh al mes que usan sus equipos con las horas al día que va a usar el
 *  motor (las escritas o las típicas: día + noche), redondeados hacia arriba como los pide el PDF (lo que usa el nivel más
 *  grande al día × 365 ÷ 12). Los paneles del aislado salen del consumo estimado: la pantalla lo propone con esto para que
 *  el vendedor no capture dos veces. null = falta un dato o no hay equipos. */
export function kwhMesDeEquiposAislado(cargas: readonly FormaCargaRespaldo[]): number | null {
  let kwhDia = 0;
  for (const c of cargas) {
    const n = numeroDe(c.cantidad);
    const w = numeroDe(c.w);
    if (n === null || w === null || !Number.isFinite(n) || !Number.isFinite(w) || n <= 0 || w <= 0) return null;
    const h = horasAisladoDe(c)?.horas_dia ?? usoDeCarga({ equipo: c.equipo }).horas_dia;
    if (!Number.isFinite(h) || h <= 0 || h > 24) return null;
    kwhDia += (n * w * h) / 1000;
  }
  return cargas.length ? Math.ceil(Math.round(kwhDia * 1000) / 1000 * 365 / 12 - 1e-9) : null;
}

/** kWh al mes que da un cuadro de cargas (para enseñarlo mientras se llena). */
export function kwhMesDeCargas(cargas: readonly FormaCarga[]): number | null {
  let total = 0;
  for (const c of cargas) {
    const n = numeroDe(c.cantidad);
    const w = numeroDe(c.w);
    const h = numeroDe(c.horas_dia);
    const m = numeroDe(c.meses_uso);
    if (n === null || w === null || h === null || m === null || [n, w, h, m].some((x) => !Number.isFinite(x))) return null;
    total += (n * w * h * 30.4 * m) / 12 / 1000;
  }
  return cargas.length ? Math.round(total) : null;
}

export type { Carga };
